import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
import type { EnergyKpiCard } from './energy_kpi_helpers';

export const CONSUMER_MIX_COLORS = [
	'#ff5a5f',
	'#31b65b',
	'#ffbd6f',
	'#b132ff',
	'#ffe500',
	'#1ccbce',
] as const;

export const DEMO_KPI_CARDS: EnergyKpiCard[] = [
	{
		id: 'load',
		icon: 'fas fa-bolt',
		value: '6.42 kW',
		infoText: 'Live load',
		color: '--color-chart-consumed-energy',
		detail: '23.7 kWh today',
		helper: '1.2 kW below evening peak',
		statusLabel: 'Peak load',
		statusValue: '7.8 kW',
		progress: 72,
		progressType: 'warning',
		showStatus: true,
	},
	{
		id: 'solar',
		icon: 'fas fa-solar-panel',
		value: '3.18 kW',
		infoText: 'Solar now',
		color: '--color-chart-generated-energy',
		detail: '12.9 kWh generated',
		helper: 'Cloud cover easing after 15:00',
		statusLabel: 'Self use',
		statusValue: '84%',
		progress: 84,
		progressType: 'success',
		showStatus: true,
	},
	{
		id: 'selfPowered',
		icon: 'fas fa-house',
		value: '84%',
		infoText: 'Self-powered',
		color: '--color-success',
		detail: 'Current demand covered by solar',
		helper: 'Based on measured solar and grid supply',
		statusLabel: 'Grid exposure',
		statusValue: '16%',
		progress: 84,
		progressType: 'success',
		showStatus: true,
	},
	{
		id: 'gridImport',
		icon: 'fas fa-plug',
		value: '620 W',
		infoText: 'Grid import',
		color: '--color-primary',
		detail: 'Importing from grid',
		helper: 'Export readings are excluded',
		statusLabel: 'Grid share',
		statusValue: '16%',
		progress: 16,
		progressType: 'primary',
		showStatus: true,
	},
	{
		id: 'periodConsumption',
		icon: 'fas fa-chart-column',
		value: '23.7 kWh',
		infoText: 'Period consumption',
		color: '--color-chart-consumed-energy',
		detail: 'Today consumption',
		helper: 'Average 0.99 kWh',
		statusLabel: 'Previous period',
		statusValue: '-8%',
		progress: 0,
		progressType: 'primary',
		showStatus: true,
	},
	{
		id: 'periodGeneration',
		icon: 'fas fa-sun',
		value: '12.9 kWh',
		infoText: 'Period generation',
		color: '--color-chart-generated-energy',
		detail: 'Today solar generation',
		helper: 'Measured local production',
		statusLabel: 'Previous period',
		statusValue: '11.8 kWh',
		progress: 0,
		progressType: 'success',
		showStatus: true,
	},
	{
		id: 'tariff',
		icon: 'fas fa-coins',
		value: 'EUR 4.86',
		infoText: 'Today',
		color: '--color-chart-money-value',
		detail: 'EUR 1.34 saved',
		helper: 'Next peak tariff at 18:00',
		statusLabel: 'Off-peak',
		statusValue: '68%',
		progress: 68,
		progressType: 'success',
		showStatus: true,
	},
	{
		id: 'currentTariff',
		icon: 'fas fa-receipt',
		value: '0.21 EUR/kWh',
		infoText: 'Current tariff',
		color: '--color-chart-money-value',
		detail: 'Off-peak',
		helper: 'Current electricity rate',
		statusLabel: 'Rate',
		statusValue: '0.21 EUR/kWh',
		progress: 0,
		progressType: 'primary',
		showStatus: true,
	},
	{
		id: 'offPeak',
		icon: 'fas fa-moon',
		value: '68%',
		infoText: 'Off-peak usage',
		color: '--color-lightblue',
		detail: 'Today at the lowest rate',
		helper: 'Based on configured tariff bands',
		statusLabel: 'Off-peak share',
		statusValue: '68%',
		progress: 68,
		progressType: 'success',
		showStatus: true,
	},
	{
		id: 'topConsumer',
		icon: 'fas fa-arrow-trend-up',
		value: '2.84 kW',
		infoText: 'Top consumer now',
		color: '--color-warning',
		detail: 'Heating & cooling',
		helper: 'Highest measured device load',
		statusLabel: 'Share of live load',
		statusValue: '44%',
		progress: 44,
		progressType: 'warning',
		showStatus: true,
	},
	{
		id: 'standby',
		icon: 'fas fa-power-off',
		value: '92 W',
		infoText: 'Standby consumption',
		color: '--color-muted',
		detail: 'Low-power devices active now',
		helper: 'Devices using up to 50 W',
		statusLabel: 'Share of live load',
		statusValue: '1%',
		progress: 1,
		progressType: 'warning',
		showStatus: true,
	},
	{
		id: 'peakDemand',
		icon: 'fas fa-chart-line',
		value: '7.8 kWh',
		infoText: 'Peak consumption',
		color: '--color-danger',
		detail: 'Highest period interval',
		helper: 'From consumption history',
		statusLabel: 'Average',
		statusValue: '1.1 kWh',
		progress: 0,
		progressType: 'danger',
		showStatus: true,
	},
];

export type DemoConsumer = {
	id: string;
	title: string;
	valueLabel: string;
	power: number;
	icon: string;
	backgroundColor: AvatarColorVariantTypes;
};

export const DEMO_CONSUMERS: DemoConsumer[] = [
	{
		id: 'demo-hvac',
		title: 'Heating & cooling',
		valueLabel: '2.84 kW · 8.4 kWh today',
		power: 2840,
		icon: 'fa-light fa-temperature-three-quarters',
		backgroundColor: 'color-danger',
	},
	{
		id: 'demo-ev',
		title: 'EV charger',
		valueLabel: '2.12 kW · smart charge',
		power: 2120,
		icon: 'fa-light fa-car-battery',
		backgroundColor: 'color-success',
	},
	{
		id: 'demo-kitchen',
		title: 'Kitchen group',
		valueLabel: '840 W · oven, fridge, plugs',
		power: 840,
		icon: 'fa-light fa-kitchen-set',
		backgroundColor: 'color-warning',
	},
	{
		id: 'demo-laundry',
		title: 'Laundry',
		valueLabel: '620 W · washer cycle',
		power: 620,
		icon: 'fa-light fa-washing-machine',
		backgroundColor: 'color-secondary',
	},
	{
		id: 'demo-lighting',
		title: 'Adaptive lighting',
		valueLabel: '286 W · 34 circuits',
		power: 286,
		icon: 'fa-light fa-lightbulb-on',
		backgroundColor: 'color-primary',
	},
];

export const DEMO_CONSUMER_MIX = {
	keys: ['hvac', 'ev', 'kitchen', 'laundry', 'lighting', 'office'],
	labels: ['Heating & cooling', 'EV charger', 'Kitchen', 'Laundry', 'Lighting', 'Home office'],
	values: [8400, 6200, 3600, 2800, 1600, 1100],
	total: 23700,
};

export type DemoInsight = {
	labelKey: string;
	labelDefault: string;
	detailKey: string;
	detailDefault: string;
	value: string;
	progress: number;
	type: 'success' | 'warning' | 'danger' | 'primary';
};

export const DEMO_INSIGHTS: DemoInsight[] = [
	{
		labelKey: 'energy_monitoring_insight_self_powered',
		labelDefault: 'Self-powered',
		detailKey: 'energy_monitoring_insight_self_powered_desc',
		detailDefault: 'Solar + battery coverage',
		value: '64%',
		progress: 64,
		type: 'success',
	},
	{
		labelKey: 'energy_monitoring_insight_grid',
		labelDefault: 'Grid exposure',
		detailKey: 'energy_monitoring_insight_grid_desc',
		detailDefault: 'Live grid reliance',
		value: '36%',
		progress: 36,
		type: 'warning',
	},
	{
		labelKey: 'energy_monitoring_insight_standby',
		labelDefault: 'Standby waste',
		detailKey: 'energy_monitoring_insight_standby_desc',
		detailDefault: 'Can be reduced overnight',
		value: '410 W',
		progress: 22,
		type: 'danger',
	},
];
